import { Injectable, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ApiService } from './api.service';

/**
 * Which vendor integrations are live for the current shop (CWS-734).
 *
 * A super-admin can pause a shop's vendor sync (shop_vendor_credentials.enabled).
 * When paused, vendor-specific UI must disappear even though the imported data is
 * still there — the customers, their memberships and their gate access all keep
 * working, only the vendor framing is hidden.
 *
 * Holds the enabled **source tags** (e.g. 'drb'), not the adapter keys
 * (e.g. 'sitewatch'), because that is what `customers.source` carries — so a
 * component can ask about the record in front of it without translating. The two
 * are deliberately different identifiers and conflating them has bitten this
 * codebase before; svc-shop does the mapping once, server-side.
 *
 * Signal-based to match RoleService, so any component can read it synchronously
 * via `isVendorSourceEnabled(...)` without subscribing.
 */
@Injectable({ providedIn: 'root' })
export class VendorModeService {
  // Fetches `company/me` itself rather than going through
  // ProfileService.getBusinessProfile(). That accessor memoizes the response in a
  // private field with shareReplay(1) and exposes no invalidator, so it answers
  // once per app session forever — which made refresh() below silently
  // impossible: it re-subscribed to the same cached observable and got the same
  // stale flags back. Owning the call keeps refresh honest.
  private readonly apiService = inject(ApiService);

  private readonly enabledSources = signal<string[]>([]);
  private readonly membershipRedeem = signal(false);
  private readonly loaded = signal(false);

  /** Enabled vendor source tags, e.g. ['drb']. Empty until loaded, and when nothing is on. */
  readonly enabledVendorSources = this.enabledSources.asReadonly();
  readonly enabledVendorSources$ = toObservable(this.enabledSources);

  /** True when at least one vendor integration is live for this shop. */
  readonly anyVendorEnabled = computed(() => this.enabledSources().length > 0);

  /**
   * CWS-1369: whether the order form offers "Redeem Existing Membership".
   *
   * Resolved server-side (`company/me` → `membershipRedeemEnabled`) from the super admin's
   * answer under Customer Migration → SiteWatch → Settings, falling back to whether the
   * vendor issues members a scannable token. Kept apart from `enabledVendorSources` on
   * purpose: that list says a sync is live, which is a different question — The Wash
   * Express and Champs both run DRB, and only Champs hands out barcode cards.
   */
  readonly membershipRedeemEnabled = this.membershipRedeem.asReadonly();

  /**
   * Whether vendor UI should show for a given `customers.source` value.
   *
   * Returns false for a Way-created customer (null/'way' source) and for any
   * vendor whose sync is paused. Safe to call before the flags have loaded — it
   * simply reports false, so vendor UI appears once rather than flickering off.
   */
  isVendorSourceEnabled(source: string | null | undefined): boolean {
    if (!source || source === 'way') {
      return false;
    }
    return this.enabledSources().includes(source);
  }

  /**
   * Populate from the shop profile. Called by the app shell (Header/SideNav),
   * mirroring how the user profile is loaded — this app has no APP_INITIALIZER.
   * Repeated calls are cheap: getBusinessProfile() is memoized with shareReplay.
   */
  loadIfNeeded(): void {
    if (this.loaded()) {
      return;
    }
    this.fetch();
  }

  /**
   * Re-read the flags now, ignoring the once-per-session guard.
   *
   * Call after toggling a vendor so the change lands without a page reload. A
   * user in a *different* browser session still needs to reload — nothing pushes
   * this to them.
   */
  refresh(): void {
    this.fetch();
  }

  private fetch(): void {
    this.loaded.set(true);
    this.apiService.get('company/me').subscribe({
      next: (response: {
        data?: { enabledVendorSources?: any; membershipRedeemEnabled?: boolean };
      }) => {
        const sources = response?.data?.enabledVendorSources;
        this.enabledSources.set(Array.isArray(sources) ? sources : []);
        this.membershipRedeem.set(response?.data?.membershipRedeemEnabled === true);
      },
      error: () => {
        // Fail closed: an unreadable shop profile hides vendor UI rather than
        // showing a chip we cannot vouch for. Clear `loaded` so the shell can
        // retry on the next navigation instead of staying dark for the session.
        this.enabledSources.set([]);
        this.membershipRedeem.set(false);
        this.loaded.set(false);
      },
    });
  }
}
